import * as THREE from 'three';
import { ModelLoader } from './ModelLoader.js';
import { AnimationManager } from './AnimationManager.js';
import { MaterialOptimizer } from './MaterialOptimizer.js';

/**
 * 数字人核心模块
 * 聚合模型加载、动画控制、材质优化三大能力
 * 外部通过此类统一操作数字人，无需关注内部细节
 */
export class HumanModel {
  /** @type {THREE.Group | null} */
  model = null;
  /** @type {AnimationManager | null} */
  animationManager = null;
  /** @type {ModelLoader} */
  #loader;
  /** @type {THREE.Box3 | null} */
  #boundingBox = null;

  /** 当前加载进度（0-100） */
  loadProgress = 0;
  /** 是否正在加载 */
  isLoading = false;

  constructor() {
    this.#loader = new ModelLoader();
  }

  /**
   * 加载数字人模型并自动完成初始化（居中、缩放、材质优化、动画注册）
   * @param {string} url - 模型文件路径
   * @param {THREE.Scene} scene - 目标场景
   * @param {object} [options]
   * @param {(progress: number) => void} [options.onProgress] - 进度回调（0-100）
   * @param {(error: Error) => void} [options.onError] - 错误回调
   * @param {object} [options.materialOptions] - 材质优化参数
   * @returns {Promise<THREE.Group>} 模型实例
   */
  async load(url, scene, options = {}) {
    const { onProgress, onError, materialOptions = {} } = options;

    this.isLoading = true;

    try {
      const { scene: model, animations } = await this.#loader.load(url, {
        onProgress: (event) => {
          this.loadProgress = event.percent ?? 0;
          onProgress?.(this.loadProgress);
        },
        onError,
      });

      this.model = model;

      // 1. 模型居中
      this.#centerModel(model);

      // 2. 自动缩放适配场景
      this.#autoScale(model);

      // 3. 材质优化
      MaterialOptimizer.optimize(model, materialOptions);

      // 4. 初始化骨骼动画
      this.animationManager = new AnimationManager(model);
      this.animationManager.initAnimationsFromClips(animations);

      // 5. 添加到场景
      scene.add(model);

      this.isLoading = false;
      this.loadProgress = 100;
      return model;
    } catch (error) {
      this.isLoading = false;
      console.error('[HumanModel] 模型加载失败:', error);
      onError?.(error);
      throw error;
    }
  }

  /** 将模型 X/Z 居中，Y 对齐到包围盒底部（脚贴地） */
  #centerModel(model) {
    this.#boundingBox = new THREE.Box3().setFromObject(model);
    const center = this.#boundingBox.getCenter(new THREE.Vector3());
    const min = this.#boundingBox.min;
    model.position.x -= center.x;
    model.position.y -= min.y;    // 脚贴地 y=0
    model.position.z -= center.z;
  }

  /** 根据包围盒大小自动缩放模型，使高度约等于 2 个场景单位 */
  #autoScale(model) {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const targetSize = 2;
      const scale = targetSize / maxDim;
      model.scale.multiplyScalar(scale);
    }
  }

  // ============ 动画控制代理方法 ============

  /** 播放指定名称的动画 */
  playAnimation(name, options = {}) {
    return this.animationManager?.play(name, options) ?? null;
  }

  /** 暂停当前动画 */
  pauseAnimation() {
    this.animationManager?.pause();
  }

  /** 恢复当前动画 */
  resumeAnimation() {
    this.animationManager?.resume();
  }

  /** 停止当前动画 */
  stopAnimation() {
    this.animationManager?.stop();
  }

  /**
   * 更新动画（每帧调用）
   * @param {number} delta
   */
  updateAnimations(delta) {
    this.animationManager?.update(delta);
  }

  /** 获取可用动画名称列表 */
  getAnimations() {
    return this.animationManager?.getAnimationNames() ?? [];
  }

  /** 获取动画详细信息（名称、时长、轨道数） */
  getAnimationDetails() {
    return this.animationManager?.getAnimationDetails() ?? [];
  }

  /** 获取当前播放的动画名称 */
  getCurrentAnimation() {
    return this.animationManager?.getCurrentAnimationName() ?? null;
  }

  /** 设置当前动画播放速度 */
  setAnimationSpeed(speed) {
    this.animationManager?.setTimeScale(speed);
  }

  /** 获取模型高度（场景单位） */
  getHeight() {
    if (!this.model) return 0;
    const box = new THREE.Box3().setFromObject(this.model);
    return box.max.y - box.min.y;
  }

  /** 获取模型胸部位置（约 45% 高度处，用于相机聚焦） */
  getChestPosition() {
    if (!this.model) return new THREE.Vector3(0, 0.8, 0);
    const box = new THREE.Box3().setFromObject(this.model);
    const height = box.max.y - box.min.y;
    return new THREE.Vector3(0, height * 0.45, 0);
  }

  /** 从场景中移除模型 */
  dispose(scene) {
    if (this.model) {
      scene.remove(this.model);
      this.model.traverse((child) => {
        if (child.isMesh) {
          child.geometry?.dispose();
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material].filter(Boolean);
          for (const mat of materials) {
            mat.dispose();
          }
        }
      });
      this.model = null;
    }
    this.animationManager = null;
  }
}
